import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { getCallsData, customNumberFormat } from "../utils";

const Chart = ({ cfs }) => {
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    const getGridValues = () => {
      const screenWidth = window.innerWidth;
      return screenWidth > 1024
        ? {}
        : {
            bottom: "25%",
          };
    };

    const orient = window.innerWidth > 1024 ? "horizontal" : "vertical";

    if (!cfs || Object.keys(cfs).length === 0) {
      return; // Exit if cfs is empty or not provided
    }

    const {
      years,
      capitalCallData,
      capitalDistributionData,
      cumulativeCFsData,
      totalCapitalCalls,
    } = getCallsData(cfs);

    const grid = getGridValues();

    setChartOptions({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          crossStyle: {
            color: "#999",
          },
        },
        formatter: function (params) {
          let tooltipContent = `${params[0].axisValueLabel}<br/>`;
          let distributionMarker = ""; // Initialize a variable to store the marker for capital distributions

          params.forEach((param) => {
            const valueFormatted = customNumberFormat(param.value);
            const percentage =
              totalCapitalCalls > 0
                ? (param.value / totalCapitalCalls) * 100
                : 0;
            const percentageFormatted = `${percentage.toFixed(1)}%`;

            if (param.seriesName === "Capital Distributions ($)") {
              // Store the marker for capital distributions
              distributionMarker = param.marker;
            }

            tooltipContent += `${param.marker}${param.seriesName}: ${valueFormatted}`;
            tooltipContent +=
              param.seriesName === "Capital Distributions ($)"
                ? `<br/>${distributionMarker}Capital Distributions (%): ${percentageFormatted}`
                : "";
            tooltipContent += `<br/>`;
          });

          return tooltipContent;
        },
      },
      legend: {
        data: [
          "Capital Calls ($)",
          "Capital Distributions ($)",
          "Capital Distributions (%)",
          "Cumulative Cash Flow",
        ],
        bottom: 0,
        orient,
      },
      grid: { ...grid },
      xAxis: [
        {
          type: "category",
          data: years.map((year) => year.slice(-4)),
          axisLabel: {
            margin: 20, // Adds spacing above the x-axis labels
          },
          axisPointer: {
            type: "shadow",
            label: {
              show: false,
            },
          },
        },
      ],
      yAxis: [
        {
          show: false,
          axisPointer: {
            show: true,
            label: {
              formatter: function (value) {
                return `$ ${customNumberFormat(value.value)}`;
              },
            },
          },
        },
        {
          show: false,
        },
      ],
      series: [
        {
          name: "Capital Calls ($)",
          type: "bar",
          data: capitalCallData,
          itemStyle: {
            color: "#D1D3D4",
          },
          label: {
            show: true,
            position: "bottom",
            formatter: function (params) {
              // Format to nearest whole number without decimals for negative values
              const valueFormatted =
                params.value < 0
                  ? `$ (${new Intl.NumberFormat().format(
                      Math.round(Math.abs(params.value))
                    )})`
                  : "";
              return valueFormatted;
            },
          },
          animation: true,
          animationDuration: 1500,
          animationEasing: "exponentialInOut",
        },
        {
          name: "Capital Distributions ($)",
          type: "bar",
          data: capitalDistributionData,
          itemStyle: {
            color: "#74BA11",
          },
          label: {
            show: true,
            position: "top",
            formatter: function (params) {
              // Ensure there's no division by zero
              const distributionValueRounded = Math.round(params.value);
              const percentage =
                totalCapitalCalls > 0
                  ? (distributionValueRounded / totalCapitalCalls) * 100
                  : 0;
              const valueFormatted = new Intl.NumberFormat().format(
                distributionValueRounded
              );
              const percentageFormatted = percentage.toFixed(1);

              return params.value > 0
                ? `{a|${percentageFormatted}%}\n\n\n$ ${valueFormatted}`
                : "";
            },
            rich: {
              a: {
                backgroundColor: "#74BA1126",
                color: "#4D4D4F",
                padding: [3, 5],
                borderRadius: 4,
              },
            },
          },
          animation: true,
          animationDuration: 1500,
          animationEasing: "exponentialInOut",
        },
        {
          name: "Cumulative Cash Flow",
          type: "line",
          yAxisIndex: 0,
          data: cumulativeCFsData,
          symbolSize: 10,
          symbol: "circle",
          smooth: true,
          itemStyle: {
            color: "#001560",
          },
          animation: true,
          animationDuration: 1500,
          animationEasing: "exponentialInOut",
        },
        {
          name: "Capital Distributions (%)",
          type: "bar",
          itemStyle: {
            color: "#74BA1126",
          },
          data: [], // ensure it doesn't actually display
          markLine: {
            show: false, // Hide mark lines
            data: [],
          },
          markPoint: {
            show: false, // Hide mark points
            data: [],
          },
          animation: false, // Disable animation
          tooltip: {
            show: false, // Disable tooltips for this series
          },
        },
      ],
    });
  }, [cfs]); // Dependency on cfs prop to recalculate when it changes

  return (
    <ReactECharts
      option={chartOptions}
      style={{
        height: window.innerWidth > 1024 ? "400px" : "600px",
        width: "100%",
      }}
    />
  );
};

export default Chart;
